// The chat's EFP panel: the same review and posting as Upload Contract, rendered in the
// assistant's side pane.
//
// It loads the allocations and positions it needs itself. The chat does not otherwise
// hold them, and a contract arrives rarely enough that fetching on demand beats keeping
// them in the conversation's state.
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import {
    AllocationModel,
    GetCustomerDataResponse,
    PositionModel,
} from '../../../../Generated/Commodity-Management-Api';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import EfpContractReview from './EfpContractReview';
import { EfpSubmitResult } from './EfpSubmitter';
import { useEfpContract } from './useEfpContract';

export interface EfpContractChatPanelProps {
    file: File;
    onPosted: (result: EfpSubmitResult) => void;
    onCancel: () => void;
}

export default function EfpContractChatPanel(props: EfpContractChatPanelProps) {
    const { file, onPosted, onCancel } = props;

    const customerData = useApplicationSelector(
        (state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData
    );

    const [allocations, setAllocations] = useState<AllocationModel[]>([]);
    const [positions, setPositions] = useState<PositionModel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const efp = useEfpContract({ customerData, allocations, positions });

    useEffect(() => {
        let cancelled = false;

        Promise.all([
            CommodityManagementApi.listAllocations(null),
            // The plan needs the futures at the parent and any basis position, so the
            // whole book is read rather than a filtered slice.
            CommodityManagementApi.listPositions(null),
        ])
            .then(([allocationResponse, positionResponse]) => {
                if (cancelled) return;
                setAllocations(allocationResponse.data.rows ?? []);
                setPositions(positionResponse.data.rows ?? []);
            })
            .catch(error => {
                if (!cancelled) setLoadError((error as Error)?.message ?? 'Could not load positions');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => { cancelled = true; };
    }, []);

    // Parse as soon as the positions are in, so the plan is built against real data.
    useEffect(() => {
        if (!isLoading && !loadError) {
            efp.loadFile(file);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, loadError, file]);

    async function handlePost() {
        const result = await efp.submit();
        onPosted(result);
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, flexGrow: 1, minWidth: 0, minHeight: 0, overflow: 'auto' }}>
            {/* Same heading as the spreadsheet path: one review section, whatever the file is. */}
            <Typography variant="h6">Review &amp; Edit Data</Typography>

            {(isLoading || efp.isParsing) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">
                        {isLoading ? 'Loading positions...' : 'Reading the contract...'}
                    </Typography>
                </Box>
            )}

            {loadError && <Alert severity="error" variant="outlined">{loadError}</Alert>}
            {efp.parseError && <Alert severity="error" variant="outlined">{efp.parseError}</Alert>}

            {efp.fields && efp.resolution && (
                <EfpContractReview
                    fileName={file.name}
                    fields={efp.fields}
                    parsedFields={efp.parsedFields ?? undefined}
                    impliedBasis={efp.impliedBasis ?? undefined}
                    resolution={efp.resolution}
                    customerData={customerData}
                    plan={efp.plan ?? undefined}
                    onChangeLeg={efp.changeLeg}
                    onChangeField={efp.changeField}
                    onChooseEntity={efp.chooseEntity}
                    onChooseBasisSource={efp.chooseBasisSource}
                    disabled={efp.isSubmitting}
                />
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button onClick={onCancel} disabled={efp.isSubmitting}>Cancel</Button>
                <Button
                    onClick={handlePost}
                    disabled={!efp.canSubmit || efp.isSubmitting}
                    startIcon={efp.isSubmitting ? <CircularProgress size={16} /> : undefined}
                >
                    {efp.isSubmitting ? 'Posting...' : 'Post EFP'}
                </Button>
            </Box>
        </Box>
    );
}
